/**
 * Table — v3 shared data table.
 *
 * Body, scrolling, virtualization, column-settings sync, and the
 * entire MRT options surface are lifted 1:1 from the legacy
 * `_legacy/components/Shared/Table/Table.jsx`. The only thing that
 * changed is the top toolbar: instead of the legacy `TopBar`
 * component, we render the v3 `TableHeader` with an icon-uniform
 * toolbar (`TableToolbarButton` + `ColumnsMenu`).
 *
 * Why a near-verbatim copy rather than a rewrite: the legacy body
 * renders smoothly on scroll, and chasing a cleaner structure from
 * scratch re-introduced flicker. Reverting to the known-good MRT
 * wiring is the safer baseline; we can refactor internals later,
 * one slice at a time, without touching scroll behaviour.
 */
import { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MantineProvider, useMantineTheme } from '@mantine/core';
import { ThemeProvider as ThemeProviderV9 } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'mui/core';
import { compare } from 'fast-json-patch';
import themeV9 from '@shared/config/theme';
import { isShimmerForTable } from '@shared/lib/tableShimmer';

// v3 shell widgets — imported from the same shared folder.
import TableHeader from './TableHeader';
import TableToolbarButton from './TableToolbarButton';
import ColumnsMenu from './ColumnsMenu';
import FilterPill from './FilterPill';
import useFilterHistory from './useFilterHistory';
import { hasFilterValue } from './lib/describeFilter';
import TableMenu from './TableMenu';
import MoreButton from './MoreButton';
import ErrorFallback from './ErrorFallback';
import EmptyFallback from './EmptyFallback';
import { RefreshIcon, FullscreenIcon, FullscreenExitIcon } from './icons';
import DensityMenu from './DensityMenu';

// Legacy-owned bits kept as-is while the rest of the table toolkit
// completes its migration out of _legacy. The body / scroll plumbing
// below relies on these — do not inline a “refactored” version
// without proving that scroll behaviour stays smooth under load.
import {
  tableBodyHoverProp,
  tableBodyProp,
  TABLE_DEFAULT_SETTINGS,
} from '@shared/ui/Table/lib/table_state_styles';
import { useForceRepaintOnVisibility } from '@shared/ui/Table/lib/helpers';

// v3 visual overrides — applied on top of `TABLE_DEFAULT_SETTINGS`'s
// plain head cell props so the column header picks up the new tokens
// without us losing any MRT internals.
import { buildV2MantineProps } from './v2Styles';
import { useHeaderLongPressReorder } from './useHeaderLongPressReorder';

import {
  updateColumnOrderByTableName,
  updateColumnSizingByTableName,
  updateColumnVisibilityByTableName,
  updateDensity,
  updateShowColumnFilters,
  updateColumnPinningByTableName,
  updateIsFullScreen,
  updateCanShowShimmer,
  resetViewedRows,
  TABLE_NAMES,
  sendNewTableSettings,
} from 'redux_store';

import './Table.css';

// NOTE: `timeoutIds` / `pendingCallbacks` are intentionally shared
// across Table instances — see the same note in the legacy component.
// Do NOT switch to useRef without understanding cross-instance debounce
// behaviour (rapid density changes on one page cancel another's pending
// settings POST). The keys (`commonTable` / `tableKey`) keep instances
// from clobbering each other's pending callbacks.
let timeoutIds = {};
let pendingCallbacks = {};
// `_delayed` mirrors the live `isLoading || isFetching` flag for the
// 300ms shimmer-show timer's stale-read check. It's per-instance — but
// the legacy code stored it module-level which makes two concurrent
// Tables (rare, but: dashboard embeds) overwrite each other.

// Mono-column heuristic — picks IDs / dates / codes / dosages per the
// design-system mono-numeral rule. Pulled out of the per-cell closure
// so we resolve the heuristic once per columns change instead of per
// (column × row) on every render. Frozen const props avoid allocating
// a fresh `{ className }` object per cell.
import { isMonoColumnId } from './lib/isMonoColumnId';

const _MONO_PROPS = Object.freeze({ className: 'qhs-cell-mono' });
const _EMPTY_PROPS = Object.freeze({});

// Stable references for MRT options that have no dynamic content.
// `useMantineReactTable` re-runs internal effects when option object
// identity changes; freezing these as module-level constants keeps
// the option set stable across renders when nothing relevant changed.
const EMPTY_DISPLAY_COLUMN_DEF_OPTIONS = Object.freeze({});
const SKELETON_PROPS = Object.freeze({ animate: false });
const SortIcon = () => <div className="sort-icon" />;
const TABLE_ICONS = Object.freeze({ IconArrowsSort: SortIcon });
const ROW_VIRTUALIZER_PROPS = Object.freeze({ overscan: 6 });
const noopRenderSkeleton = () => null;

const _isMonoColumnId = isMonoColumnId;

// Keep MRT's built-in display columns at the very edges of the pin
// arrays:
//   - mrt-row-actions → last in `right` (so it stays the rightmost
//     visible column when other columns are also right-pinned;
//     TanStack appends new pins to the end, which would otherwise
//     push mrt-row-actions away from the edge).
//   - mrt-row-select  → first in `left` (so it stays the leftmost
//     column; TanStack's left-pin already appends, which keeps
//     position 0 stable, but we normalize defensively against stale
//     persisted state).
// `hasRowActions` / `hasRowSelect` reflect whether the consumer
// actually configured those features for this Table instance.
const normalizePinning = (pinning, hasRowActions, hasRowSelect) => {
  const right = (pinning?.right || []).filter(id => id !== 'mrt-row-actions');
  const left = (pinning?.left || []).filter(id => id !== 'mrt-row-select');
  if (hasRowSelect) left.unshift('mrt-row-select');
  if (hasRowActions) right.push('mrt-row-actions');
  return { right, left };
};

const TableInner = memo(props => {
  const {
    // ---- Data + async plumbing (1:1 from legacy) ----
    columns,
    data,
    fetchNextPage,
    isError,
    fetchError,
    isFetching,
    isLoading,
    totalRowCount,
    renderRowActionMenuItems,
    sorting,
    setSorting,
    filter,
    setFilters,
    tableKey,
    enableRowSelection,
    onRowClick,
    isRowClickable,
    getRowSx,
    maxHeight,
    renderRowActions,
    rowActionsTitleSize,
    rowSelection,
    setRowSelection,
    enableEditing,
    showRefreshTableButton,
    reloadData,
    state = {},
    tableMenuPopoverOptions,
    tableMenuStatus,
    removeVerticalCellPadding = false,

    // ---- v3 header surface (new, replaces `tableTitle` +
    //      `menuActions` JSX slots) ----
    title,
    count,
    countFiltered = false,
    toolbar = [],
    toolbarExtras,
    leftToolbarExtras,
    titleActions,
    compact = false,
    excludeSortKeys,
    // Rows are filtered and sorted in memory by the caller (the whole
    // list arrived in one GET); a sort / filter change fetches nothing,
    // so the shimmer that bridges the wait for the next page must not be
    // switched on — nothing would ever switch it off again.
    localData = false,
  } = props;

  // ------------ Fix for bug with white screen
  useForceRepaintOnVisibility('.mantine-Table-root');

  const dispatch = useDispatch();
  const tableContainerRef = useRef(null);
  const totalFetched = data.length;
  const tableName = TABLE_NAMES[tableKey];
  const totalDBRowCount = totalRowCount ?? 50;
  const {
    isFullScreen,
    settings,
    settings: {
      commonTable: { density, showColumnFilters },
    },
    canShowShimmer: _canShowShimmer,
    shimmerTableKey,
  } = useSelector(s => s.table);
  const { tables: tTables } = useSelector(s => s.i18n) || {};
  const rowVirtualizerInstanceRef = useRef(null);

  // The shimmer flag is shared, so honour it only when it was armed for
  // every table (a bulk action) or by this one. Otherwise a patient
  // picker inside a panel makes the list page behind it paint skeletons
  // while that list's own data has not changed at all.
  const canShowShimmer = isShimmerForTable(_canShowShimmer, shimmerTableKey, tableKey);

  // -------------------------- Table settings from API --------------------------
  const { columnOrder, columnVisibility, columnSizes, columnPinning } = settings[tableKey] ?? {};
  // Seeded with the initial sort + filter so that the first render does
  // not count as a "sort/filter changed" event. That event arms the
  // shared shimmer flag, which is only disarmed when this table's
  // loading finishes — a table whose data is already in memory (the
  // program templates tab) never loads, and was left showing skeletons
  // until someone pressed refresh.
  const [_cashedSortAndFilter, _setCashedSortAndFilter] = useState(() =>
    JSON.stringify([...(sorting || []), ...(filter || [])])
  );
  const [_showColumnFilters, _setShowColumnFilters] = useState(showColumnFilters);
  const [_columnSizing, _setColumnSizing] = useState({ ...(columnSizes || {}) });
  const [_columnOrder, _setColumnOrder] = useState([...(columnOrder || [])]);
  const [_columnVisibility, _setColumnVisibility] = useState({
    ...(columnVisibility || {}),
  });
  const [_density, _setDensity] = useState(density || 'md');
  // mrt-row-actions / mrt-row-select are display columns whose
  // headers have no 3-dot affordance and which our toolbar
  // ColumnsMenu skips (display columns have `enableHiding: false`,
  // so they're filtered out by `col.getCanHide()`). That means once
  // persisted state somehow loses these ids from the right/left pin
  // arrays, the user has no UI path to put them back. Use
  // `normalizePinning` on mount and on every change to guarantee
  // these display columns always sit at the absolute edges, even
  // when the user pins additional data columns next to them.
  const _hasRowActions = !!renderRowActionMenuItems || !!renderRowActions;
  const _hasRowSelect = !!enableRowSelection;
  const [_columnPinning, _setColumnPinning] = useState(() =>
    normalizePinning(columnPinning, _hasRowActions, _hasRowSelect)
  );
  const _onColumnPinningChange = useCallback(
    updater => {
      _setColumnPinning(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        return normalizePinning(next, _hasRowActions, _hasRowSelect);
      });
    },
    [_hasRowActions, _hasRowSelect]
  );
  const [_isFullScreen, _setIsFullScreen] = useState(isFullScreen);
  // -----------------------------------------------------------------------------

  const { isInfoPaneExpanded, currentPage } = useSelector(s => s.infoPane);
  const isDesktop = useMediaQuery(themeV9.breakpoints.up('lg'));

  const [delayed, setDelayed] = useState(true);
  const delayedTimeoutIdRef = useRef(null);
  const isDelayedRef = useRef(false);

  // The count's skeleton follows the rows' full skeleton, not every
  // fetch: `delayed` is also true while the next page loads on scroll,
  // where the rows stay and only shimmer rows are appended. Before the
  // first page there is no count yet — the widgets pass `totalCount ?? 0`
  // — and the fetch starts a render after mount, so "0" flashed first.
  // `firstLoadDone` ends that wait even if a list answers without a
  // total (or fails).
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  const sawFetchRef = useRef(false);
  useEffect(() => {
    if (isLoading || isFetching) sawFetchRef.current = true;
    else if (sawFetchRef.current) setFirstLoadDone(true);
  }, [isLoading, isFetching]);

  const setCanShowShimmer = useCallback(
    newValue => {
      dispatch(updateCanShowShimmer({ value: newValue, tableKey }));
    },
    [dispatch, tableKey]
  );

  useEffect(() => {
    const isDelayed = isLoading || isFetching;
    isDelayedRef.current = isDelayed;
    if (delayedTimeoutIdRef.current) {
      clearTimeout(delayedTimeoutIdRef.current);
      delayedTimeoutIdRef.current = null;
    }
    if (isDelayed) {
      delayedTimeoutIdRef.current = setTimeout(() => {
        if (isDelayedRef.current) {
          setDelayed(true);
        }
      }, 300);
    } else {
      setDelayed(false);
      setCanShowShimmer(false);
    }
  }, [isLoading, isFetching, setCanShowShimmer]);

  useEffect(
    () => () => {
      if (delayedTimeoutIdRef.current) {
        clearTimeout(delayedTimeoutIdRef.current);
        delayedTimeoutIdRef.current = null;
      }
    },
    []
  );

  const debounce = (callback, wait, timeoutIdKey) => {
    return (...args) => {
      clearTimeout(timeoutIds[timeoutIdKey]);
      pendingCallbacks[timeoutIdKey] = () => callback(...args);
      timeoutIds[timeoutIdKey] = setTimeout(() => {
        delete pendingCallbacks[timeoutIdKey];
        callback(...args);
      }, wait);
    };
  };

  const updateCommonTableWithDelay = debounce(
    () => {
      // `settings[key]` can be undefined when the backend's
      // preferences store has never seen this key (e.g. a newly
      // introduced table that hasn't been added to the server-side
      // preferences schema yet). fast-json-patch's compare crashes
      // on `undefined` — fall back to an empty object so the diff
      // becomes a series of `add` ops that seed the missing entry
      // instead of throwing.
      const patches = compare(settings.commonTable || {}, {
        ...(settings.commonTable || {}),
        density: _density,
        showColumnFilters: _showColumnFilters,
      });
      dispatch(sendNewTableSettings({ path: 'commonTable', patch: patches }));
      dispatch(updateDensity(_density));
      dispatch(updateShowColumnFilters(_showColumnFilters));
    },
    2000,
    'commonTable'
  );

  const updateTableSettingsByNameWithDelay = debounce(
    () => {
      // See updateCommonTableWithDelay above — same undefined-guard
      // rationale for the per-table preferences bucket.
      const patches = compare(settings[tableKey] || {}, {
        ...(settings[tableKey] || {}),
        columnPinning: _columnPinning,
        columnSizes: _columnSizing,
        columnVisibility: _columnVisibility,
        columnOrder: _columnOrder,
      });
      dispatch(sendNewTableSettings({ path: tableKey, patch: patches }));

      dispatch(
        updateColumnSizingByTableName({
          newColumnSizing: _columnSizing,
          tableKey,
        })
      );
      dispatch(
        updateColumnPinningByTableName({
          newColumnPinning: _columnPinning,
          tableKey,
        })
      );
      dispatch(updateColumnOrderByTableName({ newOrder: _columnOrder, tableKey }));
      dispatch(
        updateColumnVisibilityByTableName({
          newVisibility: _columnVisibility,
          tableKey,
        })
      );
    },
    2000,
    tableKey
  );

  // `_data` is the array MRT actually renders: real fetched rows
  // followed by up to 30 shimmer rows. Declared here (not later, near
  // the MRT options) because `fetchMoreOnBottomReached` below uses
  // `_data.length` to find the y-coordinate of the first shimmer row,
  // and useCallback deps would otherwise hit a TDZ ReferenceError.
  const _data = useMemo(
    () => [
      ...data,
      ...(!fetchError && (totalDBRowCount !== totalFetched || (totalFetched === 0 && isFetching))
        ? Array.from(
            { length: totalFetched === 0 ? 25 : Math.min(totalDBRowCount - totalFetched, 30) },
            () => ({ showShimmer: true })
          )
        : []),
    ],
    [data, totalDBRowCount, totalFetched, fetchError, isFetching]
  );

  const fetchMoreOnBottomReached = useCallback(
    containerRefElement => {
      if (!containerRefElement || isFetching || totalFetched >= totalDBRowCount) {
        return;
      }
      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      const renderedCount = _data.length;
      const fetchedCount = data?.length || 0;
      if (fetchedCount === 0 || renderedCount === 0) return;
      // `_data` appends shimmer rows after the fetched rows. Every row
      // has the same height, so the y-coordinate where the first
      // shimmer begins is `(fetched / renderedTotal) × scrollHeight`.
      // Fire the next-page fetch the moment the viewport bottom
      // crosses that line — i.e., the first shimmer slides into view.
      // (Was a flat `1800px from bottom` threshold which fired ~30
      // rows ahead of where the user actually was, producing 3-4
      // overlapping fetches per scroll cycle.)
      const shimmerStartsAtY = (fetchedCount / renderedCount) * scrollHeight;
      if (scrollTop + clientHeight >= shimmerStartsAtY) {
        fetchNextPage(fetchedCount);
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount, data, _data]
  );

  const onIsFullScreenChange = useCallback(
    e => {
      dispatch(updateIsFullScreen({ newIsFullScreen: e }));
      _setIsFullScreen(e);
    },
    [_isFullScreen]
  );

  useEffect(() => {
    const newCashedSortAndFilter = JSON.stringify([...(sorting || []), ...(filter || [])]);
    if (_cashedSortAndFilter !== newCashedSortAndFilter) {
      if (rowVirtualizerInstanceRef.current) {
        try {
          rowVirtualizerInstanceRef.current.scrollToIndex(0);
        } catch (error) {
          console.error(error);
        }
      }
      if (!localData) setCanShowShimmer(true);
      _setCashedSortAndFilter(newCashedSortAndFilter);
    }
  }, [sorting, filter]);

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  const tableMenu = useMemo(
    () =>
      tableMenuPopoverOptions ? (
        <TableMenu
          tableMenuPopoverOptions={tableMenuPopoverOptions}
          tableMenuStatus={tableMenuStatus}
          isLoading={delayed || canShowShimmer}
        />
      ) : (
        ''
      ),
    [tableMenuPopoverOptions, delayed, tableMenuStatus]
  );

  useEffect(() => {
    updateTableSettingsByNameWithDelay();
  }, [_columnPinning, _columnSizing, _columnVisibility, _columnOrder]);

  useEffect(() => {
    updateCommonTableWithDelay();
  }, [_density, _showColumnFilters]);

  // Autofocus the first enabled text filter when the user reveals the
  // filter row (same courtesy as the create patient/assessment/order
  // panels, which focus their first field on open). Skips the initial
  // mount so a session-restored "filters shown" state does not steal
  // focus on page load. The timeout lets MRT mount the filter row
  // before we query it.
  const filtersEffectRanRef = useRef(false);
  useEffect(() => {
    const isFirstRun = !filtersEffectRanRef.current;
    filtersEffectRanRef.current = true;
    if (isFirstRun || !_showColumnFilters) return undefined;
    const timeoutId = setTimeout(() => {
      tableContainerRef.current
        ?.querySelector('thead input[data-qhs-text-filter]:not([disabled])')
        ?.focus();
    }, 60);
    return () => clearTimeout(timeoutId);
  }, [_showColumnFilters]);

  // Recent filters for the pill. `recordFilter` also fires on a click in
  // the table body: using the results is a sign the filter was the one
  // the user wanted.
  const { getRecent: getRecentFilters, record: recordFilter } = useFilterHistory({
    tableKey,
    filter,
  });

  // With a per-row clickability predicate the whole-body hover is
  // wrong (only clickable rows should highlight) — those rows get
  // their own hover via mantineTableBodyRowProps instead.
  const mantineTableBodyProps = useMemo(
    () => ({
      onClickCapture: recordFilter,
      sx: {
        ...(onRowClick && !isRowClickable ? tableBodyHoverProp : tableBodyProp),
        '& td:last-child': {
          '& .mantine-Skeleton-root': {
            marginLeft: !!renderRowActionMenuItems || !!renderRowActions ? 12 : 0,
          },
        },
        '& td:has(.more-button)': { padding: 0 },
        ...(removeVerticalCellPadding && {
          '& td': {
            paddingTop: '2px !important',
            paddingBottom: '2px !important',
            border: 'none !important',
          },
        }),
        '& tr:has(.table-fallback):hover td': {
          backgroundColor: 'transparent !important',
          cursor: 'default',
        },
        '& tr:has(.table-fallback) td': {
          backgroundColor: 'transparent !important',
          border: 'none !important',
        },
      },
    }),
    [
      delayed,
      _columnPinning,
      removeVerticalCellPadding,
      !!onRowClick,
      !!isRowClickable,
      recordFilter,
    ]
  );

  const _renderRowActions = useMemo(
    () =>
      renderRowActions
        ? renderRowActions
        : ({ row, table }) => (
            <MoreButton
              row={row}
              canShowSkeletons={table?.options?.state?.canShowSkeletons}
              actions={renderRowActionMenuItems}
            />
          ),
    [renderRowActions, renderRowActionMenuItems, delayed]
  );

  // Pre-compute the active filter count for the count badge tint.
  const activeFilterCount = useMemo(
    () => (filter || []).filter(f => hasFilterValue(f?.value)).length,
    [filter]
  );

  // ---- Top toolbar (v3 shell) ----
  // Rendered via MRT's renderTopToolbar slot. Keeps a closure over
  // the same state the legacy TopBar consumed (refresh handler,
  // filter toggle, MRT table instance for columns / density /
  // fullscreen) and routes everything through our uniform
  // TableToolbarButton icons.
  const renderTopToolbar = useCallback(
    ({ table }) => {
      const builtInToolbarExtras = (
        <>
          {showRefreshTableButton && reloadData && (
            <TableToolbarButton
              icon={<RefreshIcon />}
              title={tTables?.refreshData || 'Reload'}
              onClick={() => {
                setCanShowShimmer(true);
                try {
                  rowVirtualizerInstanceRef.current?.scrollToIndex(0);
                } catch (err) {
                  // virtualizer may not be mounted yet — fail silently
                }
                reloadData(true);
              }}
            />
          )}
          <ColumnsMenu
            table={table}
            title={tTables?.columns || 'Columns'}
            unpinAllLabel={tTables?.unpinAll || 'Unpin all'}
          />
          <DensityMenu
            density={_density}
            onChange={_setDensity}
            title={tTables?.density || 'Row density'}
          />
          <TableToolbarButton
            icon={_isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            title={
              _isFullScreen
                ? tTables?.exitFullscreen || 'Exit fullscreen'
                : tTables?.fullscreen || 'Fullscreen'
            }
            active={_isFullScreen}
            onClick={() => onIsFullScreenChange(!_isFullScreen)}
          />
        </>
      );

      return (
        <TableHeader
          title={title}
          count={count}
          countFiltered={countFiltered || activeFilterCount > 0}
          countLoading={
            canShowShimmer ||
            (totalFetched === 0 && isFetching) ||
            (!!fetchNextPage && totalRowCount == null && !firstLoadDone)
          }
          filterPill={
            !!filter && (
              <FilterPill
                filter={filter}
                setFilters={setFilters}
                columns={columns}
                showColumnFilters={_showColumnFilters}
                onToggleShowFilters={next => _setShowColumnFilters(next)}
                getRecent={getRecentFilters}
              />
            )
          }
          sorting={sorting}
          setSorting={setSorting}
          columns={columns}
          titleActions={titleActions}
          toolbarActions={toolbar}
          leftToolbarExtras={leftToolbarExtras}
          compact={compact}
          excludeSortKeys={excludeSortKeys}
          toolbarExtras={
            <>
              {toolbarExtras}
              {builtInToolbarExtras}
            </>
          }
        />
      );
    },
    [
      title,
      count,
      countFiltered,
      activeFilterCount,
      getRecentFilters,
      canShowShimmer,
      totalFetched,
      isFetching,
      fetchNextPage,
      totalRowCount,
      firstLoadDone,
      sorting,
      setSorting,
      columns,
      titleActions,
      toolbar,
      toolbarExtras,
      showRefreshTableButton,
      reloadData,
      tTables,
      filter,
      setFilters,
      _showColumnFilters,
      _density,
      _isFullScreen,
      leftToolbarExtras,
      compact,
      excludeSortKeys,
    ]
  );

  const mantineToolbarAlertBannerProps = useMemo(
    () => (isError && !fetchError ? { color: 'red', children: 'Error loading data' } : undefined),
    [isError, fetchError]
  );

  const _columns = useMemo(() => columns, [columns]);

  const tableState = useMemo(
    () => ({
      isLoading: false,
      sorting: sorting || [],
      filter: filter || [],
      showAlertBanner: isError && !fetchError,
      showProgressBars: false,
      showLoadingOverlay: false,
      showSkeletons: false,
      canShowSkeletons: canShowShimmer,
      showGlobalFilter: false,
      tableName,
      rowSelection: rowSelection ?? {},
      showColumnFilters: _showColumnFilters,
      columnOrder: _columnOrder,
      columnVisibility: _columnVisibility,
      columnSizing: _columnSizing,
      density: _density,
      columnPinning: _columnPinning,
      isFullScreen: _isFullScreen,
      ...state,
    }),
    [
      sorting,
      filter,
      isError,
      fetchError,
      canShowShimmer,
      tableName,
      rowSelection,
      _showColumnFilters,
      _columnOrder,
      _columnVisibility,
      _columnSizing,
      _density,
      _columnPinning,
      _isFullScreen,
      state,
    ]
  );

  const _hasAnyRowActions = !!renderRowActionMenuItems || !!renderRowActions;
  const displayColumnDefOptions = useMemo(
    () =>
      _hasAnyRowActions
        ? {
            'mrt-row-actions': {
              header: tableMenu,
              size: rowActionsTitleSize ?? 50,
              enablePinning: false,
              enableHiding: false,
            },
          }
        : EMPTY_DISPLAY_COLUMN_DEF_OPTIONS,
    [_hasAnyRowActions, tableMenu, rowActionsTitleSize]
  );

  const mantineTablePropsMemo = useMemo(
    () => ({ highlightOnHover: !fetchError && totalFetched > 0 }),
    [fetchError, totalFetched]
  );

  const monoColumnIds = useMemo(() => {
    const set = new Set();
    for (const c of columns || []) {
      const id = c.accessorKey || c.id;
      if (id && _isMonoColumnId(id)) set.add(id);
    }
    return set;
  }, [columns]);

  const mantineTableBodyCellProps = useCallback(
    ({ cell }) => (monoColumnIds.has(cell?.column?.id) ? _MONO_PROPS : _EMPTY_PROPS),
    [monoColumnIds]
  );

  useEffect(() => {
    return () => {
      ['commonTable', tableKey].forEach(key => {
        if (pendingCallbacks[key]) {
          clearTimeout(timeoutIds[key]);
          pendingCallbacks[key]();
          delete pendingCallbacks[key];
        }
      });
      dispatch(resetViewedRows());
      // Fullscreen is a per-mounted-table mode, but the flag lives in
      // Redux (the AppShell reads it to stretch the InfoPane overlay
      // to the viewport top). Navigating away while fullscreen would
      // otherwise leave the flag stuck on. No-op when already false.
      dispatch(updateIsFullScreen({ newIsFullScreen: false }));
    };
  }, []);

  const mantinePaperProps = useMemo(() => {
    const base = {
      maxWidth: `calc(100vw - ${
        _isFullScreen && currentPage?.uuid ? (isInfoPaneExpanded && isDesktop ? 360 : 45) : 0
      }px)`,
      transition: '0.3s',
      border: 'none',
      boxShadow: 'none',
    };
    if (maxHeight) {
      return {
        style: {
          ...base,
          minHeight: maxHeight + 100,
          height: maxHeight + 100,
          maxHeight: maxHeight + 100,
        },
      };
    }
    // Flex-column paper so the scroll container below picks up
    // the remaining height via `flex: 1` instead of the previous
    // `height: calc(100% - 60px)` hardcode — the 60 assumed a
    // single-row top toolbar and broke on narrower widths where
    // the toolbar wraps to two rows, cutting off the last table
    // rows below the container.
    return {
      style: {
        ...base,
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        height: '100%',
      },
    };
  }, [_isFullScreen, isInfoPaneExpanded, isDesktop, maxHeight]);

  const mantineTableContainerProps = useMemo(() => {
    const shared = {
      minHeight: '100px',
      overflowY: 'auto',
      overflowX: 'auto',
      scrollbarWidth: 'auto',
      msOverflowStyle: 'auto',
    };
    let heightStyle;
    if (maxHeight && !_isFullScreen) {
      heightStyle = { height: maxHeight + 60 };
    } else if (_isFullScreen) {
      heightStyle = { height: 'calc(100% - 70px)' };
    } else {
      // Flex-remainder sizing pairs with the flex-column Paper
      // above; toolbar takes its natural height (single or
      // multi-row) and the container gets everything else.
      heightStyle = { flex: '1 1 0', minHeight: 0 };
    }
    return {
      style: { ...shared, ...heightStyle },
      onScroll: event => fetchMoreOnBottomReached(event.target),
    };
  }, [
    columns,
    data,
    fetchNextPage,
    isFetching,
    totalFetched,
    totalDBRowCount,
    maxHeight,
    _isFullScreen,
  ]);

  const renderEmptyRowsFallback = useCallback(() => {
    if (isFetching || isLoading) return <></>;
    if (fetchError) {
      const hasActiveFilters = !!filter?.some(
        ({ value }) => value.toString().replaceAll(' ', '').length > 0
      );
      return (
        <ErrorFallback
          error={fetchError}
          onRetry={reloadData}
          hasActiveFilters={hasActiveFilters}
        />
      );
    }
    return <EmptyFallback />;
  }, [isFetching, isLoading, fetchError, reloadData, filter, sorting]);

  const v2Props = useMemo(
    () => buildV2MantineProps({ removeVerticalCellPadding }),
    [removeVerticalCellPadding]
  );

  // Fresh-value refs for the row-click affordance — see the comment on
  // mantineTableBodyRowProps inside the dependency-free memo below.
  const onRowClickRef = useRef(onRowClick);
  onRowClickRef.current = onRowClick;
  const isRowClickableRef = useRef(isRowClickable);
  isRowClickableRef.current = isRowClickable;
  const getRowSxRef = useRef(getRowSx);
  getRowSxRef.current = getRowSx;

  const _initSettings = useMemo(
    () => ({
      manualFiltering: !enableEditing,
      manualSorting: !enableEditing,
      enableFilters: !enableEditing,
      initialState: {
        pagination: { pageSize: 15 },
        density: 'md',
        showColumnFilters: true,
        columnPinning: {
          right: !!renderRowActionMenuItems || !!renderRowActions ? ['mrt-row-actions'] : [],
          left: !!enableRowSelection ? ['mrt-row-select'] : [],
        },
      },
      onColumnOrderChange: _setColumnOrder,
      enableRowVirtualization: false,
      enableColumnVirtualization: false,
      onColumnVisibilityChange: _setColumnVisibility,
      onColumnSizingChange: _setColumnSizing,
      onDensityChange: _setDensity,
      onColumnPinningChange: _onColumnPinningChange,
      onIsFullScreenChange,
      enableRowSelection,
      enableEditing,
      enableRowActions: !!renderRowActionMenuItems || !!renderRowActions,
      // The surrounding config memo is deliberately dependency-free, so
      // the row-props callback must read the click handler through refs:
      // capturing `onRowClick` directly would freeze its first-render
      // value (a consumer passing `canClick ? handler : undefined` after
      // data arrives would get permanently dead rows). `isRowClickable`
      // additionally scopes the affordance (cursor + hover + onClick)
      // per row.
      mantineTableBodyRowProps: enableRowSelection
        ? null
        : prop => {
            const click = onRowClickRef.current;
            const clickablePredicate = isRowClickableRef.current;
            const clickable =
              !!click && (!clickablePredicate || !!clickablePredicate(prop.row.original));
            // Per-row sx from the consumer (e.g. dimming
            // engagement-paused patients). Read through a ref for the
            // same freshness reason as the click handler. NOTE: not
            // applied in the enableRowSelection branch above, which
            // returns null row props.
            const consumerSx = getRowSxRef.current ? getRowSxRef.current(prop.row.original) : null;
            return {
              onClick: clickable ? () => click(prop.row.original) : null,
              sx: {
                ...(clickable
                  ? {
                      cursor: 'pointer',
                      ...(clickablePredicate && {
                        '&:hover td': {
                          backgroundColor: 'var(--qhs-surface-hover)',
                          transition: 'background-color 200ms ease-in',
                        },
                      }),
                    }
                  : {}),
                ...(consumerSx || {}),
              },
            };
          },
    }),
    []
  );

  const tableRef = useRef(null);
  const longPressHeadCellProps = useHeaderLongPressReorder(
    {
      getColumn: id => tableRef.current.getColumn(id),
      getState: () => tableRef.current.getState(),
      setDraggingColumn: c => tableRef.current.setDraggingColumn(c),
      setHoveredColumn: c => tableRef.current.setHoveredColumn(c),
      setColumnOrder: o => tableRef.current.setColumnOrder(o),
      getHeadCellElements: () => tableRef.current.refs.tableHeadCellRefs.current,
    },
    v2Props.tableHeadCellProps
  );

  // Filter row keyboard flow: Tab / Shift+Tab inside a filter input
  // moves straight to the next / previous filter input, like a form —
  // instead of walking every header control of the next column first.
  // Scoped to <input> elements inside <thead>, so body cells, toolbar
  // and the header sort buttons keep native tab behavior; on the last
  // (or first, with Shift) filter the default Tab exits the table.
  const handleHeadFilterTab = useCallback(event => {
    if (event.key !== 'Tab') return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.disabled) return;
    if (!target.closest('thead')) return;
    const inputs = Array.from(
      tableContainerRef.current?.querySelectorAll('thead input:not([disabled])') || []
    );
    const index = inputs.indexOf(target);
    if (index === -1) return;
    const next = inputs[index + (event.shiftKey ? -1 : 1)];
    if (!next) return;
    event.preventDefault();
    next.focus();
    next.select?.();
  }, []);

  const table = useMantineReactTable({
    ...TABLE_DEFAULT_SETTINGS,
    ..._initSettings,
    columns: _columns,
    data: _data,
    enableDensityToggle: !removeVerticalCellPadding,
    mantineTableContainerProps: {
      ref: tableContainerRef,
      onKeyDown: handleHeadFilterTab,
      ...mantineTableContainerProps,
    },
    mantineTableHeadCellProps: longPressHeadCellProps,
    // Tag mono-eligible columns with `qhs-cell-mono`. CSS in
    // Table.css picks up the class and switches that column to the
    // JetBrains Mono token (per design-system.html: mono numerals are
    // for "IDs, dates, codes, dosages").
    mantineTableBodyCellProps: mantineTableBodyCellProps,
    // Defense-in-depth alongside the right-pin re-injection elsewhere:
    // even if a future toolbar surface forgets to skip display columns,
    // MRT's getCanPin / getCanHide will return false here and block the ask.
    displayColumnDefOptions,
    mantinePaperProps,
    mantineSkeletonProps: SKELETON_PROPS,
    mantineToolbarAlertBannerProps,
    state: tableState,
    // Only enable the row-actions column when the caller actually
    // provides actions. Without this guard the wrapper would always
    // pass MoreButton (with `actions={undefined}`) and MRT would
    // render an empty 3-dot menu — visible bug for tables whose
    // row actions are permission-gated to nothing.
    renderRowActions: _hasAnyRowActions ? _renderRowActions : undefined,
    renderTopToolbar,
    mantineTableBodyProps,
    renderSkeleton: noopRenderSkeleton,
    onColumnFiltersChange: setFilters,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    renderEmptyRowsFallback,
    mantineTableProps: mantineTablePropsMemo,
    enableStickyHeader: true,
    rowVirtualizerInstanceRef,
    enableRowVirtualization: true,
    rowVirtualizerProps: ROW_VIRTUALIZER_PROPS,
    enableSortingRemoval: false,
    icons: TABLE_ICONS,
  });
  tableRef.current = table;

  return (
    <div className="qhs-table-v3">
      <MantineReactTable table={table} renderSkeleton={() => null} />
    </div>
  );
});

const queryClient = new QueryClient();

const Table = memo(props => (
  <TableWrapper columns={props?.columns}>
    <TableInner {...props} />
  </TableWrapper>
));

export default Table;

const TableWrapper = memo(({ children, columns }) => {
  const { settings } = useSelector(s => s.table);
  const globalTheme = useMantineTheme();

  if (!columns || !columns.length || !settings) return <div />;

  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={{ ...globalTheme, primaryColor: 'blue', primaryShade: 5 }}>
        <ThemeProviderV9 theme={themeV9}>{children}</ThemeProviderV9>
      </MantineProvider>
    </QueryClientProvider>
  );
});
